import React, { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { AxiosError } from "axios";
import { observer } from "mobx-react-lite";
import "./Login.scss";
import { Alert, Button, TextField } from "@mui/material";
import logo from "../../resources/logo.jpeg";
import mobileLogo from "../../resources/logo-text.png";
import googleIcon from "../../resources/google-g.png";
import slogan from "../../resources/slogan.jpeg";
import authStore from "../../stores/AuthStore";
import { loginValidations } from "./validations";
import Box from "@mui/material/Box";
import useTranslation from "../../hooks/useTranslation";
import Dropdown from "../../components/Dropdown";
import { dictionary, languageOptions } from "../../languages";
import { DropdownItem } from "../../components/Dropdown/types";
import configStore, { Flags } from "../../stores/ConfigStore";
import { useGoogleLogin } from "@react-oauth/google";
import feedbackStore from "../../stores/FeedbackStore";
import { FeedbackSeverity } from "../../stores/FeedbackStore/types";
import VerifyEmailDialog from "./VerifyEmailDialog";
import profileFormRules from "../Profile/rules";
import UserAgreementDialog from "./UserAgreementDialog";
import { LoginFormValues } from "./types";
import { AppLang } from "../../stores/ConfigStore/types";
import Captcha from "../../components/Captcha";
import { isCordova, isIOS } from "../../utils/constants";
import useGoogleOauth from "../../hooks/useGoogleOauth";
import useAppleSignIn from "../../hooks/useAppleSignIn";

enum Tab {
  LOGIN = "LOGIN",
  REGISTER = "REGISTER",
}

enum RegisterSource {
  MANUAL = "MANUAL",
  GOOGLE = "GOOGLE",
  APPLE = "APPLE",
}

const Login: React.FC = observer(() => {
  // Form
  const {
    control,
    formState: { errors },
    getValues,
    setValue,
    setError,
    clearErrors,
    handleSubmit,
    trigger,
  } = useForm<LoginFormValues>();

  // Helpers
  const { t } = useTranslation();

  // States
  const [currentTab, setCurrentTab] = useState(Tab.LOGIN);
  const [preventSpamClick, setPreventSpamClick] = useState(false);
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const [renderCaptchaHash, setRenderCaptchaHash] = useState("");
  const [verifyEmailDialogOpen, setVerifyEmailDialogOpen] = useState(false);
  const [userAgreementDialogOpen, setUserAgreementDialogOpen] = useState(false);
  const [registerSource, setRegisterSource] = useState<RegisterSource>(
    RegisterSource.MANUAL
  );
  const [registerData, setRegisterData] = useState<LoginFormValues | null>(
    null
  );

  // Internal Methods
  const resetCaptcha = useCallback(async () => {
    setRenderCaptchaHash("");
    setTimeout(() => setRenderCaptchaHash(Math.random().toString()), 100);
  }, []);

  const checkCaptcha = (): boolean => {
    if (!getValues("captchaCode") || getValues("captchaCode").length < 6) {
      console.log("Captcha code is invalid");
      setError("captchaCode", {
        type: "required",
        message: "captchaError",
      });
      return false;
    }

    return true;
  };

  const showError = (error: AxiosError, fallbackMessage?: string) => {
    let errorMessage = fallbackMessage || "error";

    if ((error.response?.data as any)?.code === "user-not-exists") {
      errorMessage = "userNotExists";
    } else if ((error.response?.data as any)?.code === "user-already-exists") {
      errorMessage = "userAlreadyExists";
    } else if ((error.response?.data as any)?.code === "user-blocked") {
      errorMessage = "userBlocked";
    } else if ((error.response?.data as any)?.code === "invalid-captcha") {
      errorMessage = "invalidCaptcha";
    } else if ((error.response?.data as any)?.code === "invalid-email") {
      errorMessage = "invalidEmail";
    }

    feedbackStore.setShowFeedback(true, {
      message: errorMessage,
      severity: FeedbackSeverity.ERROR,
    });
  };

  useEffect(() => {
    const currentLanguage =
      localStorage.getItem("preferredLanguage") ||
      navigator.language?.split("-")[0] ||
      "tr";
    dictionary.setLanguage(currentLanguage);
    resetCaptcha();
  }, [resetCaptcha]);

  /* Social Logins */
  const onAppleRegister = (
    token: string,
    givenName: string,
    familyName: string
  ) => {
    setPreventSpamClick(true);
    authStore
      .appleRegister(token, givenName, familyName)
      .then(() => window.location.replace("/"))
      .catch((error) => {
        console.error(error);
        resetCaptcha();
        setPreventSpamClick(false);
        showError(error);
      });
  };

  const onAppleLogin = (token: string) => {
    setPreventSpamClick(true);
    authStore
      .appleLogin(token)
      .then(() => window.location.replace("/"))
      .catch((error) => {
        console.error(error);
        resetCaptcha();
        setPreventSpamClick(false);
        showError(error);
      });
  };

  const onGoogleRegister = (access_token: string) => {
    setPreventSpamClick(true);
    authStore
      .googleRegister(access_token)
      .then(() => window.location.replace("/"))
      .catch((error) => {
        console.error(error);
        resetCaptcha();
        setPreventSpamClick(false);
        showError(error);
      });
  };

  const onGoogleLogin = (access_token: string) => {
    setPreventSpamClick(true);
    authStore
      .googleLogin(access_token)
      .then(() => window.location.replace("/"))
      .catch((error) => {
        console.error(error);
        resetCaptcha();
        setPreventSpamClick(false);
        showError(error);
      });
  };

  const googleWebLogin = isCordova
    ? useGoogleOauth({ onGoogleLogin })
    : useGoogleLogin({
        onSuccess: (tokenResponse) => {
          onGoogleLogin(tokenResponse.access_token);
        },
      });

  const googleWebRegister = isCordova
    ? useGoogleOauth({ onGoogleLogin: onGoogleRegister })
    : useGoogleLogin({
        onSuccess: (tokenResponse) => {
          onGoogleRegister(tokenResponse.access_token);
        },
      });

  const appleLogin = useAppleSignIn({ onAppleLogin });
  const appleRegister = useAppleSignIn({ onAppleLogin: onAppleRegister });
  /* End of Social Logins */

  /* Auth Methods */
  const submitLogin = (username: string, password: string) => {
    setPreventSpamClick(true);
    authStore
      .login(
        username,
        password,
        getValues("captchaId"),
        getValues("captchaCode")
      )
      .then(() => window.location.replace("/"))
      .catch((error) => {
        console.error(error);
        resetCaptcha().then();
        setPreventSpamClick(false);
        showError(error);
      });
  };

  const submitUserRegister = (
    email: string,
    password: string,
    phone: string
  ) => {
    setPreventSpamClick(true);
    authStore
      .registerUser(
        email,
        password,
        phone,
        getValues("captchaId"),
        getValues("captchaCode")
      )
      .then((result) => {
        if (result.code === "user-already-exists") {
          feedbackStore.setShowFeedback(true, {
            message: "userExists",
            severity: FeedbackSeverity.WARNING,
          });
        } else {
          setVerifyEmailDialogOpen(true);
        }
      })
      .catch((error) => {
        resetCaptcha().then();
        showError(error);
      })
      .finally(() => setPreventSpamClick(false));
  };

  const forgotPassword = () => {
    if (forgotPasswordSent) {
      feedbackStore.setShowFeedback(true, {
        message: "fpSuccess",
        severity: FeedbackSeverity.SUCCESS,
      });
    } else if (checkCaptcha()) {
      trigger("username").then((isValid) => {
        if (isValid) {
          setPreventSpamClick(true);
          authStore
            .forgotPassword(
              getValues("username"),
              getValues("captchaId"),
              getValues("captchaCode")
            )
            .then(() => {
              setForgotPasswordSent(true);
              feedbackStore.setShowFeedback(true, {
                message: "fpSuccess",
                severity: FeedbackSeverity.SUCCESS,
              });
            })
            .catch((error) => {
              showError(error, "fpError");
            })
            .finally(() => {
              resetCaptcha();
              setPreventSpamClick(false);
            });
        }
      });
    }
  };

  const onSubmit = (data: LoginFormValues) => {
    if (checkCaptcha()) {
      if (currentTab === Tab.LOGIN) {
        submitLogin(data.username, data.password);
      } else {
        setRegisterSource(RegisterSource.MANUAL);
        setRegisterData(data);
        setUserAgreementDialogOpen(true);
      }
    }
  };
  /* End of Auth Methods */

  /* Handlers */
  const handleOnEmailVerified = () => {
    setCurrentTab(Tab.LOGIN);
    setVerifyEmailDialogOpen(false);
    resetCaptcha().then();
    feedbackStore.setShowFeedback(true, {
      message: "registerSuccess",
      severity: FeedbackSeverity.SUCCESS,
    });
  };

  const handleGoogleSSOLogin = () => {
    if (currentTab === Tab.REGISTER) {
      setRegisterSource(RegisterSource.GOOGLE);
      setUserAgreementDialogOpen(true);
    } else {
      googleWebLogin();
    }
  };

  const handleAppleSSOLogin = () => {
    if (currentTab === Tab.REGISTER) {
      setRegisterSource(RegisterSource.APPLE);
      setUserAgreementDialogOpen(true);
    } else {
      appleLogin();
    }
  };

  const handleUserAgreementVerified = (data: LoginFormValues | null) => {
    setUserAgreementDialogOpen(false);

    if (registerSource === RegisterSource.GOOGLE) {
      googleWebRegister();
    } else if (registerSource === RegisterSource.APPLE) {
      appleRegister();
    } else if (data) {
      submitUserRegister(data.username, data.password, data.phone || "");
    }
  };

  const handleOnLanguageSelected = (item: DropdownItem) => {
    configStore.setCurrentLanguage(item.key as AppLang);
  };

  const handleCaptchaVerify = ({
    captchaId,
    captchaCode,
  }: {
    captchaId: string;
    captchaCode: string;
  }) => {
    if (captchaId) {
      clearErrors("captchaId");
    }
    if (captchaCode?.length === 6) {
      clearErrors("captchaCode");
    }
    setValue("captchaId", captchaId);
    setValue("captchaCode", captchaCode);
  };
  /* End of Handlers */

  return (
    <>
      <div className="loginContainer">
        <div className="bannerContainer">
          <img src={slogan} alt="Slogan" />
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <header>
            <div>
              <span
                className={currentTab === Tab.LOGIN ? "active" : ""}
                onClick={() => setCurrentTab(Tab.LOGIN)}
              >
                {t("login")}
              </span>
              {!configStore.getFlag(Flags.isRegistrationDisabled) && (
                <>
                  <span>&nbsp; | &nbsp;</span>
                  <span
                    className={currentTab === Tab.REGISTER ? "active" : ""}
                    onClick={() => setCurrentTab(Tab.REGISTER)}
                  >
                    {t("register")}
                  </span>
                </>
              )}
            </div>
            <div className="languagePicker">
              <Dropdown
                activeKey={configStore.currentLanguage}
                options={languageOptions}
                onItemSelected={handleOnLanguageSelected}
                placement="bottom"
              />
            </div>
          </header>
          <div className="logoContainer">
            <img src={logo} alt="logo" className="hidden-sm" />
            <img src={mobileLogo} alt="mobile logo" className="hidden-lg" />
          </div>
          <div className="mb">
            <Controller
              name={"username"}
              control={control}
              render={({ field: { onChange, value } }) => (
                <TextField
                  error={!!errors.username}
                  onChange={onChange}
                  value={value}
                  label={t("email")}
                  variant="outlined"
                />
              )}
              rules={{ ...loginValidations.username }}
            />
          </div>
          {currentTab === Tab.REGISTER && (
            <div className="mb">
              <Controller
                name={"phone"}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <TextField
                    error={!!errors.phone}
                    onChange={onChange}
                    value={value}
                    label={t("phone-number")}
                    variant="outlined"
                  />
                )}
                rules={{ ...profileFormRules["phone"] }}
              />
            </div>
          )}
          <div className="mb">
            <Controller
              name={"password"}
              control={control}
              render={({ field: { onChange, value } }) => (
                <TextField
                  error={!!errors.password}
                  onChange={onChange}
                  value={value}
                  label={t("password")}
                  type="password"
                  variant="outlined"
                />
              )}
              rules={{ ...loginValidations.password }}
            />
          </div>
          <Box
            sx={{
              position: "relative",
              width: "100%",
              textAlign: "center",
              marginBottom: "16px",
            }}
          >
            {!!renderCaptchaHash && <Captcha onChange={handleCaptchaVerify} />}
          </Box>
          <div className="loginActions mb">
            <Button
              variant="text"
              disabled={preventSpamClick}
              onClick={forgotPassword}
            >
              {currentTab === Tab.LOGIN ? t("forgotPassword") : ""}
            </Button>
            <Button type="submit" variant="text" disabled={preventSpamClick}>
              {currentTab === Tab.LOGIN ? t("login") : t("register")}
            </Button>
          </div>
          <div className="formErrors">
            {errors.username && (
              <Alert variant={"outlined"} severity="error" className="mb">
                {t("email-" + errors.username.type)}
              </Alert>
            )}
            {errors.password && (
              <Alert variant={"outlined"} severity="error" className="mb">
                {t("password-" + errors.password.type)}
              </Alert>
            )}
            {errors.phone && (
              <Alert variant={"outlined"} severity="error" className="mb">
                {errors.phone && t(`profile-phone-${errors.phone.type}`)}
              </Alert>
            )}
            {errors.captchaCode && (
              <Alert variant="outlined" severity="error" className="mb">
                {t(errors.captchaCode.message || "captchaError")}
              </Alert>
            )}
          </div>
          {isIOS ? (
            <Box
              visibility={preventSpamClick ? "hidden" : "visible"}
              className="appleLogin"
              onClick={handleAppleSSOLogin}
            >
              <Button variant="outlined" className="appleButton" fullWidth>
                {t("signInWithApple")} &nbsp;&nbsp;
                <i className="fab fa-apple" />
              </Button>
            </Box>
          ) : (
            <Box
              visibility={preventSpamClick ? "hidden" : "visible"}
              className="googleLogin"
              onClick={handleGoogleSSOLogin}
            >
              <img src={googleIcon} alt="Google" />
              <span>
                {currentTab === Tab.LOGIN
                  ? t("googleLogin")
                  : t("googleRegister")}
              </span>
            </Box>
          )}
        </form>
        <VerifyEmailDialog
          isOpen={verifyEmailDialogOpen}
          setIsOpen={setVerifyEmailDialogOpen}
          email={getValues("username")}
          onVerified={handleOnEmailVerified}
        />
        <UserAgreementDialog
          data={registerData}
          isOpen={userAgreementDialogOpen}
          setIsOpen={setUserAgreementDialogOpen}
          onVerified={handleUserAgreementVerified}
        />
      </div>
    </>
  );
});

export default Login;
